import React, { useEffect, useState } from "react";
import { ChevronDown, Loader2, Sparkles, Gavel, History } from "lucide-react";
import { Tile, Badge } from "../Primitives";
import ClassExplanation from "./ClassExplanation";
import { ClassMove, TransitionChips, SearchBox, Pager, useDebounced } from "./ChangeParts";
import { getChanges, getChangeHistory } from "../../lib/classificationApi";
import { formatPeso, formatNumber, cn } from "../../lib/utils";

const ROLE = { it_head: "IT Head", coo: "COO" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-PH", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function originOf(d) {
  if (!d) return undefined;
  if (d.mode === "actual") return { origin: "actual", source: "Nightly ABC × XYZ runs · 1Rotary actual data", updatedAt: d.created_at };
  const what = d.mode === "practice" ? "practice files — not real data" : "sample data";
  return { origin: "synthetic", source: `Nightly ABC × XYZ runs · ${what}`, updatedAt: d.created_at };
}

function AutoStatus({ a }) {
  if (!a?.total) return null;
  const busy = a.done + a.failed < a.total;
  return (
    <span data-testid="changes-auto-status" className="inline-flex items-center gap-1.5 text-[11px] text-muted">
      {busy ? <Loader2 size={11} className="animate-spin text-primary" /> : <Sparkles size={11} className="text-primary" />}
      Copilot explained the top {a.total}: <strong className="text-fg font-mono-tabular">{a.done}/{a.total}</strong>
      {a.failed > 0 && <span className="text-warning">· {a.failed} could not be written — open them to retry</span>}
    </span>
  );
}

function RuleEvents({ events }) {
  const shown = (events || []).filter(e => e.kind === "setting" || e.kind === "source_switch");
  if (!shown.length) return null;
  return (
    <div data-testid="changes-rule-events" className="space-y-1">
      {shown.map((e, i) => (
        <div key={i} className="text-[11px] text-muted flex items-start gap-1.5">
          <Gavel size={11} className="text-warning mt-0.5 shrink-0" />
          <span><strong className="text-fg">{e.kind === "setting" ? `${e.label}: ${e.before} → ${e.after}` : `Data source: ${e.before} → ${e.after}`}</strong>
            {" "}· {ROLE[e.actor_role] || e.actor} · “{e.reason}”</span>
        </div>
      ))}
    </div>
  );
}

function Empty({ d }) {
  const text = !d ? "No nightly run has been compared yet. The first nightly run sets the baseline; class changes appear here the morning after."
    : d.baseline ? "This nightly run set the baseline. Class changes appear here after the next nightly run."
    : d.source_switched ? "The data source switched between these two nightly runs, so classes were not compared. Tomorrow's summary compares like with like."
    : "No SKU changed class between the last two nightly runs.";
  return <div data-testid="changes-empty" className="py-8 text-center text-[12px] text-muted px-4">{text}</div>;
}

function ChangeRow({ c, digestId, open, onToggle }) {
  const tid = `change-row-${c.sku}`;
  return (
    <div data-testid={tid} className="border-b border-border/70 last:border-b-0">
      <button type="button" data-testid={`${tid}-toggle`} onClick={onToggle}
              className="w-full grid grid-cols-[26px_minmax(0,1fr)_auto_14px] md:grid-cols-[30px_minmax(0,1.6fr)_auto_96px_minmax(0,2fr)_14px] gap-x-3 items-center px-4 py-2 text-left hover:bg-surface-2/40 transition-colors">
        <span className="font-mono-tabular text-[10.5px] text-subtle">#{c.rank}</span>
        <div className="min-w-0">
          <div className="font-mono-tabular text-[11.5px] flex items-center gap-1.5">{c.sku}
            {c.auto && <span data-testid={`${tid}-auto`} title="Explained automatically this morning" className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest text-primary"><Sparkles size={9} /> auto</span>}
          </div>
          <div className="text-[10.5px] text-muted truncate">{c.name}</div>
        </div>
        <ClassMove from={c.from} to={c.to} testid={`${tid}-move`} />
        <span className="hidden md:block font-mono-tabular text-[11px] text-right">{formatPeso(c.value_after, { compact: true })}</span>
        <span className="hidden md:block text-[10.5px] text-muted truncate">{c.drivers[0]}</span>
        <ChevronDown size={12} className={cn("text-muted transition-transform", open && "rotate-180 text-primary")} />
      </button>
      {open && (
        <div data-testid={`${tid}-detail`} className="px-4 pb-3 pt-1 space-y-2.5 bg-surface-2/20">
          <ul className="text-[11px] text-muted space-y-0.5 list-disc pl-4">
            {c.drivers.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
          <div className="text-[10.5px] text-subtle font-mono-tabular">Annual value {formatPeso(c.value_before, { compact: true })} → {formatPeso(c.value_after, { compact: true })}</div>
          <ClassExplanation kind="change" id={`${digestId}/${c.sku}`} testid={`${tid}-explain`} title="Why it moved · Copilot"
                            generateLabel="Explain this change" idleHint="Copilot explains this change from the two nightly runs' figures. Saved with this summary."
                            className="border border-primary/30 bg-primary/[0.04] rounded-sm p-3" />
        </div>
      )}
    </div>
  );
}

function Header({ d, history, picked, onPick }) {
  return (
    <div className="px-4 py-3 border-b border-border space-y-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <span data-testid="changes-headline" className="text-[13px] font-semibold">
          {formatNumber(d.changes_count)} SKUs changed class
          <span className="text-muted font-normal"> · {formatPeso(d.value_moved, { compact: true })} annual value</span>
        </span>
        <span className="text-[11px] text-muted font-mono-tabular" data-testid="changes-period">{fmt(d.period_start)} → {fmt(d.period_end)}</span>
        <AutoStatus a={d.auto_explain} />
        {history?.length > 1 && (
          <label className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-muted"><History size={11} />
            <select data-testid="changes-history-select" value={picked || ""} onChange={e => onPick(e.target.value || null)}
                    className="bg-bg border border-border rounded-sm px-2 py-1 text-[11px] focus:outline-none focus:border-primary">
              {history.map((h, i) => <option key={h.id} value={i === 0 ? "" : h.id}>{fmt(h.created_at)} · {h.changes_count} changes</option>)}
            </select>
          </label>
        )}
      </div>
      <RuleEvents events={d.rule_events} />
    </div>
  );
}

// Morning Brief: what changed class overnight (top 20 explained automatically, the rest on demand).
export default function ClassChangeAlerts() {
  const [picked, setPicked] = useState(null);
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const [transition, setTransition] = useState(null);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => { getChangeHistory(7).then(h => setHistory(h.digests)).catch(() => setHistory([])); }, []);
  useEffect(() => { setPage(1); }, [dq, transition, picked]);
  useEffect(() => { setTransition(null); setQ(""); setOpen(null); }, [picked]);
  useEffect(() => {
    let alive = true;
    getChanges({ q: dq, transition, page, size: 50 }, picked)
      .then(x => { if (alive) { setData(x); setError(null); } })
      .catch(e => alive && setError(e.message));
    return () => { alive = false; };
  }, [dq, transition, page, picked, tick]);
  const a = data?.digest?.auto_explain;
  const busy = a && a.total && a.done + a.failed < a.total;
  useEffect(() => {
    if (!busy) return undefined;
    const t = setTimeout(() => setTick(n => n + 1), 5000);
    return () => clearTimeout(t);
  }, [busy, data]);

  const d = data?.digest;
  return (
    <Tile testid="class-changes" dense origin={originOf(d)} title="Overnight class changes · ABC × XYZ"
          action={d && <Badge status={d.changes_count ? "warning" : "success"} testid="changes-count">{formatNumber(d.changes_count)} changed</Badge>}>
      {!data && !error && <div className="py-8 text-center text-[12px] text-muted"><Loader2 size={13} className="inline animate-spin mr-2" />Loading overnight changes…</div>}
      {error && <div data-testid="changes-error" className="m-4 text-[12px] text-critical border border-critical/30 bg-critical/5 rounded-sm p-3">{error}</div>}
      {data && (!d || !d.changes_count) && <>{d && <Header d={d} history={history} picked={picked} onPick={setPicked} />}<Empty d={d} /></>}
      {d && d.changes_count > 0 && (
        <>
          <Header d={d} history={history} picked={picked} onPick={setPicked} />
          <div className="px-4 py-2.5 flex flex-wrap items-center gap-2 border-b border-border">
            <TransitionChips transitions={d.transitions} active={transition} onPick={setTransition} testid="changes-transition" />
            <SearchBox value={q} onChange={setQ} testid="changes-search" />
          </div>
          <div data-testid="changes-list">
            {data.rows.map(c => <ChangeRow key={c.sku} c={c} digestId={d.id} open={open === c.sku} onToggle={() => setOpen(o => (o === c.sku ? null : c.sku))} />)}
            {data.rows.length === 0 && <div data-testid="changes-no-match" className="py-6 text-center text-[12px] text-muted">No changed SKU matches “{dq}”.</div>}
          </div>
          <Pager data={data} onPage={setPage} testid="changes-page" />
        </>
      )}
    </Tile>
  );
}
